<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useRouteColumnFilter, useODataSource, useDefaultEditing, useDefaultGridOptions, refreshGridAsync, getCreatedRowId, refreshChangedRowAsync } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import { useArchivedAtColumn, useArchivedColumn, useAvatarColumn } from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { useRoiCompareBasket } from '@/composables/roi-comparison'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import CompanyEdit from '@/components/shared/CompanyEdit.vue'
import CompanyGridDetail from '@/components/planner/CompanyGridDetail.vue'
import RoiComparisonBasket from '@/components/roi/RoiComparisonBasket.vue'

const gridId = 'companies-grid-v2'

const i18n = getI18n()

const { gridRef } = useGridRef()
const { onContentReady } = useRouteColumnFilter(gridRef)
const editItem = ref(null)

const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const basket = useRoiCompareBasket(planningStore.roiComparisonTypeEnum.COMPANY, gridRef)
const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.companiesUrl, {
  customParamsFn: () => { return showArchived.value ? { show_archived: true } : {} }
})

const { archiveWarning, archiveButton, showArchived, archiveAsync, onRowPrepared: onArchiveRowPrepared, onRowRemoving } = useArchive({
  onArchive: async(id) => { 
    await planningStore.archiveCompanyAsync(id) 
    await planningStore.fetchListingsAsync(null, true)
  },
  onDelete: async(id, config) => { await planningStore.deleteCompanyAsync(id, config) },
  gridRef: gridRef,
})

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      { 
        name: 'edit', 
        visible: (e) => !e.row?.data?.archived,  
        onClick: (e) => { editItem.value = { ...e.row.data } }, 
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '6rem',
  },
  useAvatarColumn(),
  {
    dataField: 'code',
    caption: i18n.t('common.code'),
    width: '6rem',
    sortOrder: 'asc',
  },
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    width: '8rem',
  },
  {
    dataField: 'description',
    caption: i18n.t('common.description'),
    width: '15rem',
  },
  {
    dataField: 'address',
    caption: i18n.t('common.address'),
    width: '14rem',
  },
  {
    dataField: 'vat_number',
    caption: i18n.t('common.vatNumber'),
    width: '10rem',
  },
  {
    dataField: 'is_client',
    caption: i18n.t('common.client'),
    dataType: 'boolean',
    width: '6rem',
  },
  {
    dataField: 'is_rental',
    caption: i18n.t('common.rental'),
    dataType: 'boolean',
  },
  useArchivedAtColumn(),
  useArchivedColumn(),
]


const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns: columns,
  dataSource: gridDataSource,
  editing: useDefaultEditing(),
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  onRowRemoving: onRowRemoving,
  onRowPrepared: (e) => { onArchiveRowPrepared(e); basket.onRowPrepared(e) },
  selection: webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS) ? { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' } : undefined,
  onSelectionChanged: basket.onSelectionChanged,
  extraToolbarItems: [basket.toolbarItem],
  onContentReady: onContentReady,
  clearFilterButton: true,
  archivedToggle: true,
  addButton: {
    onClick: () => {
      editItem.value = {}
    },
  },
})

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}


useEntityChanges([entityChangeTypeEnum.COMPANY], (change) => refreshChangedRowAsync(gridRef, change), () => editItem.value !== null)
</script>
<template>
  <div>
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <company-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <roi-comparison-basket :basket="basket" />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: templateData }">
        <company-grid-detail :company="templateData.data" />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
